import { useState, useEffect } from "react";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase/config";
import toast from "react-hot-toast";

const DOWNLOADABLE_CONTENT_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const DOWNLOADABLE_EXTENSIONS = [".pdf", ".doc", ".docx"];

function isDownloadableDocument(file) {
  if (!file) return false;
  const type = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  if (DOWNLOADABLE_CONTENT_TYPES.some((t) => type.includes(t))) return true;
  return DOWNLOADABLE_EXTENSIONS.some((ext) => name.endsWith(ext));
}

function getUploadMetadata(file, options = {}) {
  const metadata = { contentType: file.type || undefined };
  if (options.contentDisposition) {
    const filename = file.name ? `; filename="${encodeURIComponent(file.name)}"` : "";
    metadata.contentDisposition = `${options.contentDisposition}${filename}`;
  }
  return metadata;
}

export function useLeadMagnets() {
  const [leadMagnets, setLeadMagnets] = useState([]);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "leadMagnet"),
      (snapshot) => {
        const fetched = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data()?.createdAt?.toDate() || new Date(),
        })).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setLeadMagnets(fetched);
      }
    );
    return () => unsubscribe();
  }, []);

  const saveLeadMagnet = async (data, editingLead, onProgress) => {
    const report = (p) => typeof onProgress === "function" && onProgress(Math.round(p));
    report(0);

    const id = data.id || data.uuid;
    let contentUrlToSave = data.contentUrl ?? editingLead?.contentUrl ?? null;
    let thumbnailUrlToSave = editingLead?.thumbnailUrl ?? null;

    if (data.contentFile) {
      const contentRef = ref(storage, `leadmagnet/${id}/content`);
      const contentMetadata = isDownloadableDocument(data.contentFile)
        ? getUploadMetadata(data.contentFile, { contentDisposition: "attachment" })
        : getUploadMetadata(data.contentFile);
      const contentEnd = data.thumbnailFile ? 80 : 95;
      await new Promise((resolve, reject) => {
        const task = uploadBytesResumable(contentRef, data.contentFile, contentMetadata);
        task.on(
          "state_changed",
          (snapshot) => {
            const p = (snapshot.bytesTransferred / snapshot.totalBytes) * contentEnd;
            report(p);
          },
          reject,
          () => resolve()
        );
      });
      contentUrlToSave = await getDownloadURL(contentRef);
    }
    if (data.thumbnailFile) {
      report(80);
      const thumbRef = ref(storage, `leadmagnet/${id}/thumbnail`);
      const thumbMetadata = getUploadMetadata(data.thumbnailFile);
      await new Promise((resolve, reject) => {
        const task = uploadBytesResumable(thumbRef, data.thumbnailFile, thumbMetadata);
        task.on(
          "state_changed",
          (snapshot) => {
            const p = 80 + (snapshot.bytesTransferred / snapshot.totalBytes) * 15;
            report(p);
          },
          reject,
          () => resolve()
        );
      });
      thumbnailUrlToSave = await getDownloadURL(thumbRef);
    }
    
    // Process formations offertes and upload download files
    let formationsOffertesToSave = data.formationsOffertes || null;
    if (formationsOffertesToSave && formationsOffertesToSave.length > 0) {
      report(85);
      const totalFormations = formationsOffertesToSave.length;
      let processedCount = 0;
      
      formationsOffertesToSave = await Promise.all(
        formationsOffertesToSave.map(async (formation, formationIdx) => {
          if (!formation.downloads || formation.downloads.length === 0) {
            return formation;
          }
          
          const downloadsWithUrls = await Promise.all(
            formation.downloads.map(async (download, downloadIdx) => {
              // If no new file is uploaded, keep existing data (URL, label, etc.)
              if (!download.file) {
                return {
                  label: download.label || 'Download',
                  url: download.url || null,
                };
              }
              
              // Upload new file
              const fileRef = ref(
                storage,
                `leadmagnet/${id}/formations/${formation.id || formationIdx}/downloads/${download.id || downloadIdx}-${Date.now()}-${download.file.name}`
              );
              const fileMetadata = getUploadMetadata(download.file, { 
                contentDisposition: `attachment; filename="${encodeURIComponent(download.file.name)}"` 
              });
              
              await new Promise((resolve, reject) => {
                const task = uploadBytesResumable(fileRef, download.file, fileMetadata);
                task.on(
                  "state_changed",
                  () => {
                    const progress = 85 + ((processedCount + (downloadIdx + 1) / formation.downloads.length) / totalFormations) * 10;
                    report(Math.min(progress, 95));
                  },
                  reject,
                  () => resolve()
                );
              });
              
              const downloadUrl = await getDownloadURL(fileRef);
              processedCount += 1 / formation.downloads.length;
              
              return {
                label: download.label || download.file.name,
                url: downloadUrl,
              };
            })
          );
          
          return {
            ...formation,
            downloads: downloadsWithUrls,
          };
        })
      );
    }
    
    report(95);

    const formFields = {
      formTitle: data.formTitle ?? null,
      formSubtitleLine1: data.formSubtitleLine1 ?? null,
      formSubtitleLine2: data.formSubtitleLine2 ?? null,
      ctaButton1Link: data.ctaButton1Link ?? null,
      ctaButton2Link: data.ctaButton2Link ?? null,
      ctaButton1Label: data.ctaButton1Label ?? null,
      ctaButton2Label: data.ctaButton2Label ?? null,
    };

    const payload = {
      title: data.title,
      description: data.description,
      contentUrl: contentUrlToSave,
      thumbnailUrl: thumbnailUrlToSave ?? null,
      lead_magnet_url: data.lead_magnet_url,
      ...formFields,
      formationsOffertes: formationsOffertesToSave,
    };

    if (editingLead) {
      await updateDoc(doc(db, "leadMagnet", id), {
        ...payload,
        updatedAt: serverTimestamp(),
      });
      toast.success("Lead magnet updated successfully!");
    } else {
      await setDoc(doc(db, "leadMagnet", data.uuid), {
        ...payload,
        uuid: data.uuid,
        createdAt: serverTimestamp(),
      });
      toast.success("Lead magnet created successfully!");
    }
    report(100);
  };

  const deleteLeadMagnet = async (id) => {
    await deleteDoc(doc(db, "leadMagnet", id));
    toast.success("Lead magnet deleted");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("URL copied to clipboard!");
  };

  return {
    leadMagnets,
    saveLeadMagnet,
    deleteLeadMagnet,
    copyToClipboard,
  };
}
